import { useEffect, useState } from 'react';
import Product from '@/models/product';
import { useProduct, useCreateProduct, useUpdateProduct, useDeleteProduct, useStripePrices, ProductMutationPayload } from '@/services/products';
import FormModal from '@/shared/components/form-modal';
import DropZoneField from '@/shared/components/form/dropzones/DropZone';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { errorBannerStyle } from '@/shared/dark-inline-styles';

interface ProductModalProps {
    isOpen: boolean;
    selectedProductId?: string;
    toggle: () => void;
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

interface ProductForm {
    slug: string;
    name: string;
    tagline: string;
    description: string;
    image: string;
    stripePriceId: string;
    stripeProductId: string;
    comingSoon: boolean;
    active: boolean;
    orderIndex: number | '';
}

const emptyForm = (): ProductForm => ({
    slug: '',
    name: '',
    tagline: '',
    description: '',
    image: '',
    stripePriceId: '',
    stripeProductId: '',
    comingSoon: false,
    active: true,
    orderIndex: 0,
});

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>{children}</p>
);

const ProductModal = ({ isOpen, toggle, selectedProductId }: ProductModalProps) => {
    const { data: product, isLoading } = useProduct(selectedProductId as string, isOpen);
    const { mutateAsync: createProduct, isPending: isCreating } = useCreateProduct();
    const { mutateAsync: updateProduct, isPending: isUpdating } = useUpdateProduct();
    const { mutateAsync: deleteProduct, isPending: isDeleting } = useDeleteProduct();
    const { data: pricesRes, isLoading: isPricesLoading } = useStripePrices(isOpen);
    const stripePrices = pricesRes?.data ?? [];

    const [form, setForm] = useState<ProductForm>(emptyForm());
    const [error, setError] = useState<string | null>(null);
    const [uploadingCount, setUploadingCount] = useState(0);
    const onUploadingChange = (uploading: boolean) => setUploadingCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    const isUploading = uploadingCount > 0;

    // `isOpen` in the deps so re-opening the SAME product re-fills the form from
    // the cached data (the close-reset below would otherwise leave it blank).
    useEffect(() => {
        if (!isOpen) return;
        if (product?.data) {
            const p: Product = product.data;
            setForm({
                slug: p.slug,
                name: p.name,
                tagline: p.tagline ?? '',
                description: p.description,
                image: p.image ?? '',
                stripePriceId: p.stripePriceId ?? '',
                stripeProductId: p.stripeProductId ?? '',
                comingSoon: !!p.comingSoon,
                active: p.active !== false,
                orderIndex: typeof p.orderIndex === 'number' ? p.orderIndex : 0,
            });
        } else if (!selectedProductId) {
            setForm(emptyForm());
        }
        setError(null);
    }, [isOpen, product?.data, selectedProductId]);

    useEffect(() => {
        if (!isOpen) {
            setForm(emptyForm());
            setError(null);
            setUploadingCount(0);
        }
    }, [isOpen]);

    const set = <K extends keyof ProductForm>(key: K, value: ProductForm[K]) => setForm((f) => ({ ...f, [key]: value }));

    const onSelectPrice = (priceId: string) => {
        const opt = stripePrices.find((p) => p.id === priceId);
        setForm((f) => ({ ...f, stripePriceId: priceId, stripeProductId: opt?.productId ?? f.stripeProductId }));
    };

    const selectedPrice = stripePrices.find((p) => p.id === form.stripePriceId);

    const validate = (): string | null => {
        if (!form.name.trim()) return 'Name is required';
        if (!form.slug.trim()) return 'Slug is required';
        if (!SLUG_RE.test(form.slug.trim())) return 'Slug must be lowercase letters, numbers and hyphens only';
        if (!form.description.trim()) return 'Description is required';
        // A product that's live (active, not coming-soon) MUST map to a Stripe
        // price, or checkout would refuse it. Block the broken state up front.
        if (form.active && !form.comingSoon && !form.stripePriceId) {
            return 'Select a Stripe price (so it can be purchased), or mark the product as Coming soon / set it to Hidden.';
        }
        if (form.orderIndex !== '' && !Number.isInteger(Number(form.orderIndex))) {
            return 'Display order must be a whole number.';
        }
        return null;
    };

    const buildPayload = (): ProductMutationPayload => ({
        slug: form.slug.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        tagline: form.tagline.trim() || undefined,
        image: form.image || undefined,
        stripePriceId: form.stripePriceId || undefined,
        stripeProductId: form.stripeProductId || undefined,
        comingSoon: form.comingSoon,
        active: form.active,
        orderIndex: form.orderIndex === '' ? 0 : Number(form.orderIndex),
    });

    const handleSave = async () => {
        if (isUploading) {
            setError('Please wait — an image is still uploading.');
            return;
        }
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            const payload = buildPayload();
            // callApi never throws on API errors — inspect result.status.
            const result = selectedProductId ? await updateProduct({ productId: selectedProductId, ...payload }) : await createProduct(payload);
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to save the product.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to save product:', e);
            setError('Failed to save the product. Check the console for details.');
        }
    };

    const handleDelete = async () => {
        if (!selectedProductId) return;
        try {
            const result = await deleteProduct({ productId: selectedProductId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete the product.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete product:', e);
            setError('Failed to delete the product. Check the console for details.');
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!selectedProductId}
            title={`${selectedProductId ? 'Edit' : 'New'} Product`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <div style={errorBannerStyle}>{error}</div>}

                    <MaterialTextField label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} />

                    <MaterialTextField label="Slug (URL: /accessories/your-slug)" value={form.slug} onChange={(e) => set('slug', e.target.value)} />
                    <Hint>Short, lowercase, hyphenated. The stable id the storefront uses.</Hint>

                    <MaterialTextField label="Tagline (one-line card subtitle)" value={form.tagline} onChange={(e) => set('tagline', e.target.value)} />

                    <MaterialTextField label="Description" multiline value={form.description} onChange={(e) => set('description', e.target.value)} />

                    {/* ── Pricing (live from Stripe) ───────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Stripe price</p>
                    <Hint>
                        Pricing lives in Stripe — pick the price here and the storefront always shows the live amount + currency. Create the product &
                        price in the Stripe dashboard first if it isn’t listed.
                    </Hint>
                    <MaterialTextField name="select" select label="Stripe price" value={form.stripePriceId} onChange={(e) => onSelectPrice(e.target.value)}>
                        <MaterialFormOption value="">— No price (coming soon) —</MaterialFormOption>
                        {stripePrices.map((p) => (
                            <MaterialFormOption key={p.id} value={p.id}>
                                {p.productName} — {p.formatted ?? `${p.currency.toUpperCase()}`} ({p.id})
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>
                    {isPricesLoading && <Hint>Loading Stripe prices…</Hint>}
                    {!isPricesLoading && stripePrices.length === 0 && (
                        <Hint>No active Stripe prices found. Create a product + price in the Stripe dashboard, then refresh.</Hint>
                    )}
                    {selectedPrice && (
                        <Hint>
                            Live price: <strong>{selectedPrice.formatted ?? '—'}</strong> ({selectedPrice.currency.toUpperCase()}). Changes you make in
                            Stripe apply instantly — no re-save needed.
                        </Hint>
                    )}

                    {/* ── Image ────────────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Image</p>
                    <Hint>Primary product image shown on the storefront card + detail page.</Hint>
                    <DropZoneField
                        customHeight
                        usePermanentUrl
                        uploadPath="accessories/images"
                        accept="image/jpeg, image/jpg, image/png, image/webp"
                        currentValue={form.image}
                        currentFileType="image"
                        onChange={(image) => set('image', image)}
                        onUploadingChange={onUploadingChange}
                    />

                    {/* ── Visibility ───────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Visibility</p>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <MaterialTextField
                            name="select"
                            select
                            label="Status"
                            value={form.active ? 'active' : 'hidden'}
                            onChange={(e) => set('active', e.target.value === 'active')}
                        >
                            <MaterialFormOption value="active">Active (shown in storefront)</MaterialFormOption>
                            <MaterialFormOption value="hidden">Hidden (admin only)</MaterialFormOption>
                        </MaterialTextField>
                        <MaterialTextField
                            name="select"
                            select
                            label="Availability"
                            value={form.comingSoon ? 'coming' : 'available'}
                            onChange={(e) => set('comingSoon', e.target.value === 'coming')}
                        >
                            <MaterialFormOption value="available">Available to buy</MaterialFormOption>
                            <MaterialFormOption value="coming">Coming soon (not purchasable)</MaterialFormOption>
                        </MaterialTextField>
                        <MaterialTextField
                            label="Display order"
                            type="number"
                            inputProps={{ step: 1 }}
                            value={form.orderIndex}
                            onChange={(e) => set('orderIndex', e.target.value === '' ? '' : Number(e.target.value))}
                        />
                    </div>
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isCreating || isUpdating || isDeleting}
            saveDisabled={isUploading}
            saveDisabledHint="Uploading image… please wait until it finishes before saving."
        />
    );
};

export default ProductModal;
